import { useCallback, useEffect, useMemo, useState } from "react";
import type { ConnectProto } from "@racoon/api";
import {
  JOB_INCIDENT_SAVE,
  useCreateJobIncidentMutation,
  useListDriverIncidentsQuery,
} from "~/shared/api/driver-incidents/driver-incidents.grpc";

type Answered = {
  /** The list the answers belong to; another one starts over. */
  key: string;
  /** Job ids asked about and answered (decimal strings, as the cache holds ids). */
  jobIds: ReadonlySet<string>;
  incidents: readonly ConnectProto.DriverIncident[];
};

const none = (key: string): Answered => ({ key, jobIds: new Set(), incidents: [] });

/**
 * The incidents of the registry's loaded jobs, asked for a page at a time: only the jobs
 * not asked about yet go out, so each scrolled page costs one request for its own jobs
 * rather than one for every job loaded so far. Starts over when `listKey` changes.
 *
 * Returns, per job, the incidents to read it from (`jobIncidentNote` picks its own), or
 * undefined until its answer arrives, so a job is never shown as without one too early.
 */
export const useRegistryIncidents = ({
  ownerOrganizationId,
  jobs,
  listKey,
  skip,
}: {
  ownerOrganizationId: bigint;
  jobs: readonly ConnectProto.Job[];
  listKey: string;
  skip: boolean;
}) => {
  const [stored, setStored] = useState<Answered>(() => none(listKey));
  const answered = useMemo(
    () => (stored.key === listKey ? stored : none(listKey)),
    [stored, listKey],
  );

  const pending = useMemo(
    () => jobs.filter((job) => !answered.jobIds.has(String(job.id))).map((job) => BigInt(job.id)),
    [jobs, answered.jobIds],
  );
  const { currentData, originalArgs } = useListDriverIncidentsQuery(
    { ownerOrganizationId, jobIds: pending },
    { skip: skip || pending.length === 0 },
  );

  useEffect(() => {
    if (!currentData || !originalArgs) return;
    setStored((previous) => {
      const base = previous.key === listKey ? previous : none(listKey);
      const known = new Set(base.incidents.map((incident) => String(incident.id)));
      return {
        key: listKey,
        jobIds: new Set([...base.jobIds, ...(originalArgs.jobIds ?? []).map(String)]),
        incidents: [
          ...base.incidents,
          ...(currentData.driverIncidents ?? []).filter(
            (incident) => !known.has(String(incident.id)),
          ),
        ],
      };
    });
  }, [currentData, originalArgs, listKey]);

  // A reason saved from a job's report: an answered job is never asked about again
  const [, { data: recorded }] = useCreateJobIncidentMutation({ fixedCacheKey: JOB_INCIDENT_SAVE });
  useEffect(() => {
    if (!recorded) return;
    setStored((previous) =>
      previous.incidents.some((incident) => String(incident.id) === String(recorded.id))
        ? previous
        : { ...previous, incidents: [...previous.incidents, recorded] },
    );
  }, [recorded]);

  return useCallback(
    (job: ConnectProto.Job) =>
      answered.jobIds.has(String(job.id)) ? answered.incidents : undefined,
    [answered],
  );
};
